import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { UsersThree, FloppyDisk, PaperPlaneTilt, X, CheckCircle } from "@phosphor-icons/react";

const ReparPage = () => {
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [users, setUsers] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);

    const load = async (d) => {
        setLoading(true);
        try {
            const [usersRes, asgRes] = await Promise.all([
                axios.get(`${API}/users/list`),
                axios.get(`${API}/assignments`, { params: { date: d } }),
            ]);
            setUsers(usersRes.data || []);
            setRows(asgRes.data.assignments || []);
        } catch (e) {
            toast.error("Error al cargar el reparto");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(date); }, [date]);

    const userName = (id) => users.find((u) => u.id === id)?.name || id;

    // Operarios que ya están en alguna obra
    const assignedIds = new Set(rows.flatMap((r) => r.user_ids || []));
    // Bolsa: operarios (ni admin ni jefe de obra) que aún no tienen obra
    const pool = users.filter((u) => u.role !== "admin" && !u.is_jefe && !assignedIds.has(u.id));

    const assign = (projectId, userId) => {
        if (!userId) return;
        setRows((prev) =>
            prev.map((r) => {
                if (r.project_id === projectId) {
                    return { ...r, user_ids: [...new Set([...r.user_ids, userId])], is_today: true };
                }
                // Defensa: si estuviera en otra obra, sacarlo de ahí
                if ((r.user_ids || []).includes(userId)) {
                    const notes = { ...(r.notes || {}) };
                    delete notes[userId];
                    return { ...r, user_ids: r.user_ids.filter((x) => x !== userId), notes, is_today: true };
                }
                return r;
            })
        );
    };

    const unassign = (projectId, userId) => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.project_id !== projectId) return r;
                const notes = { ...(r.notes || {}) };
                delete notes[userId];
                return { ...r, user_ids: r.user_ids.filter((x) => x !== userId), notes, is_today: true };
            })
        );
    };

    const setNote = (projectId, userId, text) => {
        setRows((prev) =>
            prev.map((r) =>
                r.project_id === projectId
                    ? { ...r, notes: { ...(r.notes || {}), [userId]: text }, is_today: true }
                    : r
            )
        );
    };

    const saveAll = () =>
        Promise.all(
            rows.map((r) =>
                axios.put(`${API}/assignments`, { date, project_id: r.project_id, user_ids: r.user_ids, notes: r.notes || {} })
            )
        );

    const save = async () => {
        setSaving(true);
        try {
            await saveAll();
            toast.success("Reparto guardado");
            load(date);
        } catch (e) {
            toast.error("Error al guardar el reparto");
        } finally {
            setSaving(false);
        }
    };

    const send = async () => {
        setSending(true);
        try {
            await saveAll();
            const res = await axios.post(`${API}/assignments/send`, { date });
            const { enviados, jefes_avisados, sin_canal } = res.data;
            toast.success(`Reparto enviado a ${enviados} operario(s) y ${jefes_avisados || 0} jefe(s) de obra`);
            if (sin_canal && sin_canal.length > 0) {
                toast.warning(`Sin canal (no recibieron): ${sin_canal.join(", ")}`);
            }
            load(date);
        } catch (e) {
            toast.error("Error al enviar el reparto");
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
    }

    return (
        <div className="space-y-6 animate-in" data-testid="repar-page">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#0055FF] rounded-sm flex items-center justify-center">
                        <UsersThree size={28} weight="fill" className="text-white" />
                    </div>
                    <div>
                        <h1 className="font-heading font-bold text-2xl md:text-3xl">Reparto de operarios</h1>
                        <p className="text-[#555555]">Asigna a cada operario a una obra. Al asignarlo sale de la bolsa; cuando la bolsa queda vacía, están todos repartidos.</p>
                    </div>
                </div>
                <div className="flex items-end gap-3">
                    <div>
                        <Label className="label-overline mb-2 block">Fecha</Label>
                        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-industrial" />
                    </div>
                    <Button onClick={save} disabled={saving || sending} variant="outline">
                        <FloppyDisk size={18} className="mr-2" />
                        {saving ? "Guardando..." : "Guardar"}
                    </Button>
                    <Button onClick={send} disabled={saving || sending} className="btn-primary">
                        <PaperPlaneTilt size={18} className="mr-2" />
                        {sending ? "Enviando..." : "Enviar a operarios"}
                    </Button>
                </div>
            </div>

            {/* Bolsa de operarios sin asignar */}
            <div className={`border rounded-sm p-4 ${pool.length === 0 ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
                <div className="flex items-center gap-2 mb-3">
                    {pool.length === 0 ? (
                        <CheckCircle size={20} weight="fill" className="text-green-600" />
                    ) : (
                        <UsersThree size={20} weight="fill" className="text-amber-600" />
                    )}
                    <h2 className="font-heading font-bold">
                        {pool.length === 0
                            ? "Todos los operarios tienen obra asignada"
                            : `Sin asignar (${pool.length})`}
                    </h2>
                </div>
                {pool.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {pool.map((u) => (
                            <span key={u.id} className="inline-flex items-center px-3 py-1 rounded-full bg-white border border-amber-300 text-sm">
                                {u.name}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {rows.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] rounded-sm p-12 text-center text-[#555555]">
                    No hay obras activas.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {rows.map((r) => (
                        <div key={r.project_id} className="bg-white border border-[#E5E5E5] rounded-sm flex flex-col">
                            <div className="p-4 border-b border-[#E5E5E5] flex items-center justify-between">
                                <h2 className="font-heading font-bold">{r.project_name}</h2>
                                <span className="text-xs font-bold px-2 py-1 rounded-sm bg-[#F0F4FF] text-[#0055FF]">
                                    {r.user_ids.length} operario{r.user_ids.length === 1 ? "" : "s"}
                                </span>
                            </div>
                            {!r.is_today && r.from_date && (
                                <p className="px-4 pt-2 text-xs text-[#999]">Heredado del {r.from_date}</p>
                            )}
                            <div className="p-4 space-y-2 flex-1">
                                {r.user_ids.length === 0 ? (
                                    <p className="text-sm text-[#999]">Sin operarios asignados.</p>
                                ) : (
                                    r.user_ids.map((uid) => (
                                        <div key={uid} className="border border-[#EEE] rounded-sm p-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-sm font-medium">{userName(uid)}</span>
                                                <button
                                                    onClick={() => unassign(r.project_id, uid)}
                                                    className="p-1 text-[#999] hover:text-red-600 hover:bg-red-50 rounded-sm"
                                                    title="Quitar de la obra (vuelve a la bolsa)"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                            <input
                                                type="text"
                                                value={(r.notes && r.notes[uid]) || ""}
                                                onChange={(e) => setNote(r.project_id, uid, e.target.value)}
                                                placeholder="Instrucción (opcional)"
                                                className="input-industrial mt-1 w-full text-xs py-1"
                                            />
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="p-4 pt-0">
                                <Select value="" onValueChange={(v) => assign(r.project_id, v)}>
                                    <SelectTrigger className="input-industrial">
                                        <SelectValue placeholder="+ Añadir operario" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {pool.length === 0 ? (
                                            <div className="px-3 py-2 text-sm text-[#999]">No quedan operarios en la bolsa</div>
                                        ) : (
                                            pool.map((u) => (
                                                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                            ))
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReparPage;
